import { v4 as uuidv4 } from 'uuid';
import { QuoteDTO, QuoteSectionDTO } from '@setvi/shared/services/react-query';
import { useQuoteMutation } from './create-quote-mutation';
import { moveItemToSectionOptimistically } from '../utils/quote-helpers';

interface AddSectionAndMoveItemVariables {
  name: string;
  description?: string;
  itemId: string;
}

/**
 * Add section and move item to it optimistically
 * Reuses existing helper functions to avoid code duplication
 */
const addSectionAndMoveItemOptimistically = (
  quote: QuoteDTO | undefined,
  { name, description, itemId }: AddSectionAndMoveItemVariables
): QuoteDTO | undefined => {
  if (!quote) return quote;

  // Step 1: Create new section with a unique ID
  const newSectionId = uuidv4();
  const newSection: QuoteSectionDTO = {
    id: newSectionId,
    type: 'section',
    name,
    note: description || null,
    children: []
  };

  // Step 2: Add the section to the quote
  const quoteWithNewSection: QuoteDTO = {
    ...quote,
    items: [...quote.items, newSection]
  };

  // Step 3: Move the item to the newly created section
  return moveItemToSectionOptimistically(
    quoteWithNewSection,
    itemId,
    newSectionId
  );
};

/**
 * Add section and move item to it with optimistic updates
 * Section appears immediately with the item inside it
 */
export const useAddSectionAndMoveItem = (quoteId: string) =>
  useQuoteMutation<AddSectionAndMoveItemVariables>({
    quoteId,
    optimisticUpdate: addSectionAndMoveItemOptimistically
  });
